import { useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { toast } from "sonner";
import { humanMinutes } from "../PeriodPicker";
import { CalendarCheck, EnvelopeSimple, Globe, UserPlus } from "@phosphor-icons/react";

export const LEAD_STATUSES = ["new", "contacted", "quoted", "won", "lost"];
export const LOST_REASONS = [
  "No response", "Price", "No vehicle available", "Dates unavailable",
  "Booked elsewhere", "Not a real lead", "Other",
];

// Minutes between the lead landing and the first reply. "—" when never contacted,
// so it is left out of averages rather than guessed at.
export const responseText = (lead) => {
  if (!lead?.first_contacted_at || !lead?.created_at) return "—";
  const mins = (new Date(lead.first_contacted_at) - new Date(lead.created_at)) / 60000;
  return mins >= 0 ? humanMinutes(mins) : "—";
};

// LEAD-003: how a lead came in, as one small grey icon in front of the
// "#41 · Rental · Source" line. Same line, same height, no colour; the words
// after it are unchanged. The title and the hidden label say it in words.
export const leadChannel = (lead) => {
  if (lead?.origin === "booking") return { Icon: CalendarCheck, label: "Made from a booking" };
  if (lead?.origin === "website" || /website/i.test(String(lead?.source || ""))) return { Icon: Globe, label: "Website booking form" };
  if (lead?.imported) return { Icon: EnvelopeSimple, label: "Email from the site's contact form" };
  return { Icon: UserPlus, label: "Added in the CRM" };
};

export function LeadSourceIcon({ lead, testid }) {
  const { Icon, label } = leadChannel(lead);
  return (
    <span className="inline-flex align-[-2px] mr-1.5 text-muted-foreground" title={label} data-testid={testid}>
      <Icon size={13} aria-hidden="true" /><span className="sr-only">{label}. </span>
    </span>
  );
}

const STATUS_TONE = {
  new: "text-blue-300", contacted: "text-emerald-300", quoted: "text-primary",
  won: "text-emerald-400", lost: "text-red-400",
};

export function AssignedToCell({ lead, team, onSaved }) {
  const [saving, setSaving] = useState(false);
  const current = (lead.salesperson || "").trim();
  const options = current && !team.some((n) => n.toLowerCase() === current.toLowerCase()) ? [current, ...team] : team;

  const save = async (name) => {
    setSaving(true);
    try {
      await api.post(`/leads/${lead.id}/assign`, { salesperson: name === "__none" ? "" : name });
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Select value={current || "__none"} onValueChange={save} disabled={saving}>
      <SelectTrigger className="h-8 bg-transparent border-border/60 text-xs w-[150px]" data-testid={`lead-assign-${lead.id}`}>
        <SelectValue placeholder="Unassigned" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="__none">— Unassigned —</SelectItem>
        {options.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

export function StatusCell({ lead, onSaved }) {
  const [saving, setSaving] = useState(false);
  const save = async (status) => {
    setSaving(true);
    try {
      if (status === "lost") {
        await api.post("/leads/bulk", { ids: [lead.id], action: "lost", lost_reason: "Other" });
      } else {
        await api.post("/leads/bulk", { ids: [lead.id], action: "status", status });
      }
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <Select value={(lead.status || "new").toLowerCase()} onValueChange={save} disabled={saving}>
      <SelectTrigger className={`h-8 bg-transparent border-border/60 w-[140px] label-mono ${STATUS_TONE[(lead.status || "new").toLowerCase()] || ""}`} data-testid={`lead-status-${lead.id}`}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {LEAD_STATUSES.map((s) => <SelectItem key={s} value={s}>{s.toUpperCase()}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}
